/**
 * Geometria del recuadro de escaneo. Vive en la capa eager (sin ZXing) porque es
 * matematica pura y la comparte el CSS del recuadro con el filtro del decoder:
 * una sola fuente de verdad garantiza que lo que el operario ve sea exactamente
 * el area que se acepta.
 */

export interface ScanRegion {
  /** Ancho del recuadro como fraccion del ancho visible del video (0-1). */
  widthRatio: number
  /** Alto del recuadro como fraccion del alto visible del video (0-1). */
  heightRatio: number
}

export const DEFAULT_SCAN_REGION: ScanRegion = { widthRatio: 0.8, heightRatio: 0.45 }

export interface CropRect {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Traduce el recuadro (expresado en fracciones de la caja CSS del video) a
 * pixeles intrinsecos del frame, que es el sistema de coordenadas en el que
 * ZXing devuelve los ResultPoint.
 *
 * Tiene que compensar el `object-fit: cover` del video: con cover el frame se
 * escala para llenar la caja y el excedente se recorta, asi que la porcion
 * visible del frame es mas chica que el frame completo.
 */
export const cropRectFor = (
  video: HTMLVideoElement | null,
  region: ScanRegion
): CropRect | null => {
  if (!video) return null

  const { videoWidth, videoHeight, clientWidth, clientHeight } = video
  if (!videoWidth || !videoHeight || !clientWidth || !clientHeight) return null

  // cover escala para llenar la caja: gana la dimension que necesita mas aumento
  const scale = Math.max(clientWidth / videoWidth, clientHeight / videoHeight)

  // El recuadro mide una fraccion de la caja CSS; se divide por la escala para
  // pasarlo a pixeles del frame original.
  const width = (clientWidth * region.widthRatio) / scale
  const height = (clientHeight * region.heightRatio) / scale

  // Tanto la porcion visible como el recuadro estan centrados, asi que el
  // recorte queda centrado sobre el frame intrinseco.
  return {
    x: (videoWidth - width) / 2,
    y: (videoHeight - height) / 2,
    width,
    height
  }
}

/**
 * true si todos los puntos del codigo caen dentro del recuadro.
 *
 * Se exige que entre completo y no solo su centro: si el codigo asoma por fuera,
 * lo que se leyo puede ser un vecino parcialmente visible. La tolerancia absorbe
 * el error de redondeo entre la caja CSS y los pixeles del frame.
 */
export const isInsideRect = (
  points: Array<{ getX: () => number; getY: () => number }>,
  rect: CropRect,
  tolerance = 8
): boolean => {
  // Sin puntos no hay forma de ubicar la lectura: se acepta en vez de trabar el
  // escaneo, porque algunos formatos no reportan ResultPoint.
  if (!points || points.length === 0) return true

  const left = rect.x - tolerance
  const top = rect.y - tolerance
  const right = rect.x + rect.width + tolerance
  const bottom = rect.y + rect.height + tolerance

  return points.every((point) => {
    const x = point.getX()
    const y = point.getY()
    return x >= left && x <= right && y >= top && y <= bottom
  })
}
